import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  createAdminUser,
  deleteAdminUser,
  getAdminUserDetail,
  getAdminUsers,
  updateAdminUser,
} from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { unscopedKey } from "@/lib/queryClient";
import type { AdminUser } from "@/lib/types";

/**
 * M8c G1–G6 — `/admin/users`.
 *
 * Operator accounts management for global admins. Renders a list of
 * every signed-up user across all tenants with inline Edit / Toggle
 * (disable/enable) / Delete actions, an Add User modal, and a
 * detail drawer (G3) that shows the full 360 view (profile +
 * cross-tenant memberships + linked players) via the
 * `/api/users/{id}` endpoint.
 *
 * G6 — the per-row "View teams" expander is a lightweight
 * collapsed alternative to opening the full detail drawer; it
 * fetches the SAME `/api/users/{id}` payload (so the drawer's
 * memberships and the expander's memberships are coherent) and
 * caches it for the page's lifetime.
 */

const PLATFORM_ROLES = ["admin", "uploader", "viewer"] as const;

/** Shared TanStack Query options for the user-detail payload. Both
 * the G3 detail drawer AND the G6 inline expander subscribe to the
 * same key + queryFn + staleTime — without this, the drawer
 * defaulted to `staleTime: 0` and refetched immediately after the
 * expander already had a fresh result, breaking the
 * "one-fetch-shared-by-both-surfaces" contract. Re-review P2 fix. */
function useAdminUserDetail(userId: string) {
  return useQuery({
    queryKey: unscopedKey("admin-user-detail", { userId }),
    queryFn: () => getAdminUserDetail(userId),
    staleTime: 5 * 60_000,
  });
}

export function AdminUsersPage() {
  const queryClient = useQueryClient();
  const usersQuery = useQuery({
    queryKey: unscopedKey("admin-users"),
    queryFn: getAdminUsers,
  });

  const users = usersQuery.data ?? [];
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AdminUser | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const [detailTarget, setDetailTarget] = useState<AdminUser | null>(null);
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.username.toLowerCase().includes(q) ||
        (u.display_name ?? "").toLowerCase().includes(q),
    );
  }, [users, search]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: unscopedKey("admin-users") });

  return (
    <div data-testid="admin-users" className="flex flex-col gap-3">
      <header className="flex items-baseline justify-between flex-wrap gap-2">
        <h2 className="text-base font-display uppercase tracking-wider text-ink">
          Users
        </h2>
        <div className="flex items-center gap-2">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            data-testid="admin-users-search"
            className="rounded border border-rule bg-surface px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-accent"
          />
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            data-testid="admin-users-new"
            className="rounded border border-accent/40 text-accent hover:bg-accent/10 px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider"
          >
            Add user
          </button>
          <span className="text-[0.6rem] font-mono text-ink-muted">
            {usersQuery.isFetching ? "Refreshing…" : `${filtered.length} of ${users.length}`}
          </span>
        </div>
      </header>

      {usersQuery.error ? (
        <p
          data-testid="admin-users-error"
          className="text-destructive font-mono text-xs"
        >
          Failed to load users.
        </p>
      ) : null}

      <div className="rounded-lg border border-rule bg-bg-card/40">
        {filtered.length === 0 ? (
          <p
            data-testid="admin-users-empty"
            className="text-xs font-mono text-ink-muted p-3"
          >
            No users.
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-rule/60">
            {filtered.map((u) => (
              <UserRow
                key={u.id}
                user={u}
                expanded={expandedUserId === u.id}
                onToggleExpand={() =>
                  setExpandedUserId((id) => (id === u.id ? null : u.id))
                }
                onOpenDetail={() => setDetailTarget(u)}
                onEdit={() => setEditTarget(u)}
                onDelete={() => setDeleteTarget(u)}
              />
            ))}
          </ul>
        )}
      </div>

      {createOpen ? (
        <CreateUserModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            void invalidate();
          }}
        />
      ) : null}

      {editTarget ? (
        <EditUserModal
          user={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            void invalidate();
          }}
        />
      ) : null}

      {deleteTarget ? (
        <DeleteUserConfirm
          user={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            setDeleteTarget(null);
            void invalidate();
          }}
        />
      ) : null}

      {detailTarget ? (
        <UserDetailModal
          user={detailTarget}
          onClose={() => setDetailTarget(null)}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// G1 — list row + G6 inline expander
// ---------------------------------------------------------------------------

function UserRow({
  user,
  expanded,
  onToggleExpand,
  onOpenDetail,
  onEdit,
  onDelete,
}: {
  user: AdminUser;
  expanded: boolean;
  onToggleExpand: () => void;
  onOpenDetail: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const roles = user.roles ?? splitRoleString(user.role);
  return (
    <li
      data-testid="admin-users-row"
      data-user-id={user.id}
      data-enabled={user.enabled ? "true" : "false"}
      className="flex flex-col"
    >
      <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-center px-3 py-2 text-xs">
        <div className="min-w-0">
          <div className="text-ink truncate">
            {user.display_name || user.username}{" "}
            <span className="text-ink-muted">@{user.username}</span>
          </div>
          <div className="flex flex-wrap items-center gap-1 mt-0.5">
            {roles.map((r) => (
              <RolePill key={r} role={r} />
            ))}
            {/* Re-review P2: derive the Global-admin indicator from
             * the parsed `roles` array. `/api/users` returns
             * `_scrub_user(db_row)` which has `role` (comma-joined
             * string) but NOT a computed `is_global_admin` flag —
             * that lives only on `/api/me`. Reading `user.is_global_admin`
             * here meant the pill never rendered. Matches legacy
             * `renderUsersList` which derived the indicator from the
             * comma-joined role string. */}
            {roles.includes("admin") ? (
              <span
                data-testid="admin-users-global-admin-pill"
                className="rounded border border-accent/40 bg-bg-card/40 px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider text-accent"
              >
                Global admin
              </span>
            ) : null}
            <span
              data-testid="admin-users-status-pill"
              data-enabled={user.enabled ? "true" : "false"}
              className={`rounded border bg-bg-card/40 px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider ${
                user.enabled
                  ? "border-good/40 text-good"
                  : "border-rule text-ink-muted"
              }`}
            >
              {user.enabled ? "Active" : "Disabled"}
            </span>
          </div>
        </div>
        <div className="inline-flex gap-1">
          <button
            type="button"
            onClick={onToggleExpand}
            aria-expanded={expanded}
            data-testid="admin-users-expand"
            className="rounded border border-rule text-ink-muted hover:text-ink hover:bg-surface px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider"
          >
            {expanded ? "Hide teams" : "View teams"}
          </button>
        </div>
        <div className="inline-flex gap-1">
          <button
            type="button"
            onClick={onOpenDetail}
            data-testid="admin-users-detail"
            className="rounded border border-rule text-ink-muted hover:text-ink hover:bg-surface px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider"
          >
            Details
          </button>
          <button
            type="button"
            onClick={onEdit}
            data-testid="admin-users-edit"
            className="rounded border border-rule text-ink-muted hover:text-ink hover:bg-surface px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={onDelete}
            data-testid="admin-users-delete"
            className="rounded border border-destructive/40 text-destructive hover:bg-destructive/10 px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider"
          >
            Delete
          </button>
        </div>
      </div>
      {expanded ? <UserMembershipsExpander userId={user.id} /> : null}
    </li>
  );
}

function RolePill({ role }: { role: string }) {
  const tone =
    role === "admin"
      ? "border-accent/40 text-accent"
      : role === "uploader"
        ? "border-good/40 text-good"
        : "border-rule text-ink-muted";
  return (
    <span
      data-testid="admin-users-role-pill"
      data-role={role}
      className={`rounded border bg-bg-card/40 px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider ${tone}`}
    >
      {role}
    </span>
  );
}

// G6 — Memberships expander. Reuses the `/api/users/{id}` endpoint
// (same payload as the detail drawer) so the two surfaces stay
// coherent. Cached by user id via the shared `useAdminUserDetail`
// hook — opening then re-opening doesn't refetch, AND opening the
// drawer after the expander reuses the same fresh cache (re-review
// P2 fix: both observers must use the same staleTime).
function UserMembershipsExpander({ userId }: { userId: string }) {
  const query = useAdminUserDetail(userId);

  if (query.isLoading) {
    return (
      <div className="px-3 pb-2 text-[0.6rem] font-mono text-ink-muted">
        Loading teams…
      </div>
    );
  }
  if (query.error) {
    return (
      <div
        data-testid="admin-users-memberships-error"
        className="px-3 pb-2 text-[0.6rem] font-mono text-destructive"
      >
        Failed to load memberships.
      </div>
    );
  }
  const memberships = query.data?.memberships ?? [];
  if (memberships.length === 0) {
    return (
      <div
        data-testid="admin-users-memberships-empty"
        className="px-3 pb-2 text-[0.6rem] font-mono text-ink-muted"
      >
        No team memberships.
      </div>
    );
  }
  return (
    <ul
      data-testid="admin-users-memberships"
      className="px-3 pb-2 flex flex-col gap-1"
    >
      {memberships.map((m) => (
        <li
          key={m.id}
          data-testid="admin-users-membership-row"
          data-team-id={m.team_id}
          className="grid grid-cols-[1fr_auto] gap-2 items-center rounded border border-rule/60 bg-surface/40 px-2 py-1 text-[0.65rem]"
        >
          <span className="text-ink truncate">
            {m.team_name}{" "}
            <span className="font-mono text-ink-muted">· {m.team_slug}</span>
          </span>
          <span
            data-role={m.role}
            className="rounded border border-rule bg-bg-card/40 px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider text-ink-muted"
          >
            {m.role.replace("_", " ")}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// G2 — Add user modal
// ---------------------------------------------------------------------------

function CreateUserModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [roles, setRoles] = useState<string[]>(["viewer"]);
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      createAdminUser({
        username,
        password,
        role: roles.length > 0 ? roles.join(",") : "viewer",
        display_name: displayName,
      }),
    onSuccess: onCreated,
    onError: (err) => setError(formatErr(err)),
  });

  return (
    <ConfirmDialog
      title="Add user"
      message={
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Username
            </span>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              minLength={2}
              maxLength={50}
              data-testid="admin-users-new-username"
              className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Display name
            </span>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={100}
              data-testid="admin-users-new-display-name"
              className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Password
            </span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              data-testid="admin-users-new-password"
              className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </label>
          <div className="flex flex-col gap-1">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Roles (at least one)
            </span>
            <div className="flex flex-wrap gap-1">
              {PLATFORM_ROLES.map((r) => {
                const checked = roles.includes(r);
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => {
                      setRoles((rs) =>
                        checked
                          ? rs.filter((x) => x !== r)
                          : Array.from(new Set([...rs, r])),
                      );
                    }}
                    data-testid={`admin-users-new-role-${r}`}
                    data-active={checked ? "true" : undefined}
                    className="rounded border px-3 py-1 text-xs font-display uppercase tracking-wider transition-colors border-rule text-ink-muted hover:text-ink hover:bg-surface data-[active=true]:border-accent data-[active=true]:text-accent data-[active=true]:bg-accent/10"
                  >
                    {r}
                  </button>
                );
              })}
            </div>
          </div>
          {error ? (
            <p
              data-testid="admin-users-new-error"
              className="text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={mut.isPending ? "Creating…" : "Create user"}
      onConfirm={() => {
        setError(null);
        if (!username.trim() || username.length < 2) {
          setError("Username must be at least 2 characters.");
          return;
        }
        if (password.length < 8) {
          setError("Password must be at least 8 characters.");
          return;
        }
        if (roles.length === 0) {
          setError("Pick at least one role.");
          return;
        }
        mut.mutate();
      }}
      onClose={onClose}
      data-testid="admin-users-new-modal"
    />
  );
}

// ---------------------------------------------------------------------------
// G4 — Edit user modal
// ---------------------------------------------------------------------------

function EditUserModal({
  user,
  onClose,
  onSaved,
}: {
  user: AdminUser;
  onClose: () => void;
  onSaved: () => void;
}) {
  const initialRoles = user.roles ?? splitRoleString(user.role);
  const [displayName, setDisplayName] = useState(user.display_name);
  const [roles, setRoles] = useState<string[]>(initialRoles);
  const [enabled, setEnabled] = useState(user.enabled);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const dirty =
    displayName !== user.display_name ||
    enabled !== user.enabled ||
    roles.join(",") !== initialRoles.join(",") ||
    password.length > 0;

  const mut = useMutation({
    mutationFn: () =>
      updateAdminUser(user.id, {
        display_name:
          displayName !== user.display_name ? displayName : undefined,
        enabled: enabled !== user.enabled ? enabled : undefined,
        role:
          roles.join(",") !== initialRoles.join(",") ? roles.join(",") : undefined,
        password: password.length >= 8 ? password : undefined,
      }),
    onSuccess: onSaved,
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        setError(
          typeof err.detail === "string" && err.detail === "last_admin"
            ? "Cannot demote or disable the only enabled global admin."
            : typeof err.detail === "string"
              ? err.detail
              : "Last-admin protection: cannot demote the only enabled global admin.",
        );
      } else {
        setError(formatErr(err));
      }
    },
  });

  return (
    <ConfirmDialog
      title="Edit user"
      message={
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Username (read-only)
            </span>
            <input
              type="text"
              value={user.username}
              disabled
              data-testid="admin-users-edit-username"
              className="rounded border border-rule bg-surface/50 px-2 py-1.5 text-xs text-ink-muted font-mono cursor-not-allowed"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Display name
            </span>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={100}
              data-testid="admin-users-edit-display-name"
              className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </label>
          <div className="flex flex-col gap-1">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Roles
            </span>
            <div className="flex flex-wrap gap-1">
              {PLATFORM_ROLES.map((r) => {
                const checked = roles.includes(r);
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => {
                      setRoles((rs) =>
                        checked
                          ? rs.filter((x) => x !== r)
                          : Array.from(new Set([...rs, r])),
                      );
                    }}
                    data-testid={`admin-users-edit-role-${r}`}
                    data-active={checked ? "true" : undefined}
                    className="rounded border px-3 py-1 text-xs font-display uppercase tracking-wider transition-colors border-rule text-ink-muted hover:text-ink hover:bg-surface data-[active=true]:border-accent data-[active=true]:text-accent data-[active=true]:bg-accent/10"
                  >
                    {r}
                  </button>
                );
              })}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setEnabled((v) => !v)}
            data-testid="admin-users-edit-enabled"
            data-active={enabled ? "true" : undefined}
            className="self-start rounded border px-3 py-1.5 text-xs font-display uppercase tracking-wider transition-colors border-rule text-ink-muted hover:text-ink hover:bg-surface data-[active=true]:border-accent data-[active=true]:text-accent data-[active=true]:bg-accent/10"
          >
            {enabled ? "Account enabled" : "Account disabled"}
          </button>
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Password reset (optional)
            </span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              placeholder="Leave blank to keep current"
              data-testid="admin-users-edit-password"
              className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            />
            <span className="text-[0.55rem] font-mono text-ink-muted">
              Setting a new password revokes the user's other sessions on next
              login.
            </span>
          </label>
          {error ? (
            <p
              data-testid="admin-users-edit-error"
              className="text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={mut.isPending ? "Saving…" : "Save changes"}
      onConfirm={() => {
        setError(null);
        if (!dirty) {
          onClose();
          return;
        }
        if (password.length > 0 && password.length < 8) {
          setError("Password must be at least 8 characters.");
          return;
        }
        if (roles.length === 0) {
          setError("Pick at least one role.");
          return;
        }
        mut.mutate();
      }}
      onClose={onClose}
      data-testid="admin-users-edit-modal"
    />
  );
}

// ---------------------------------------------------------------------------
// G5 — Delete user confirm
// ---------------------------------------------------------------------------

function DeleteUserConfirm({
  user,
  onClose,
  onDeleted,
}: {
  user: AdminUser;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () => deleteAdminUser(user.id),
    onSuccess: onDeleted,
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        setError(
          typeof err.detail === "string" && err.detail === "last_admin"
            ? "Cannot delete the only enabled global admin."
            : typeof err.detail === "string"
              ? err.detail
              : "Last-admin protection: cannot delete the only enabled global admin.",
        );
      } else {
        setError(formatErr(err));
      }
    },
  });
  return (
    <ConfirmDialog
      title="Delete user?"
      message={
        <>
          <p>
            <strong>{user.display_name || user.username}</strong> (@
            {user.username}) will be permanently deleted along with their
            team memberships, player links, and active sessions.
          </p>
          {error ? (
            <p
              data-testid="admin-users-delete-error"
              className="mt-2 text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </>
      }
      confirmLabel={mut.isPending ? "Deleting…" : "Delete user"}
      danger
      onConfirm={() => {
        setError(null);
        mut.mutate();
      }}
      onClose={onClose}
      data-testid="admin-users-delete-confirm"
    />
  );
}

// ---------------------------------------------------------------------------
// G3 — User detail drawer (modal-style)
// ---------------------------------------------------------------------------

function UserDetailModal({
  user,
  onClose,
}: {
  user: AdminUser;
  onClose: () => void;
}) {
  // Re-review P2 fix: share the same query options as the inline
  // G6 expander so opening the drawer after the expander reuses
  // the cached payload instead of triggering an immediate refetch.
  const query = useAdminUserDetail(user.id);

  return (
    <ConfirmDialog
      title={`Details — ${user.display_name || user.username}`}
      confirmLabel="Close"
      message={
        query.isLoading ? (
          <p className="text-ink-muted font-mono text-xs">Loading…</p>
        ) : query.error ? (
          <p
            data-testid="admin-users-detail-error"
            className="text-destructive font-mono text-xs"
          >
            {formatErr(query.error)}
          </p>
        ) : query.data ? (
          <div className="flex flex-col gap-3 text-xs">
            <section data-testid="admin-users-detail-profile">
              <h4 className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted mb-1">
                Profile
              </h4>
              <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 font-mono text-[0.65rem]">
                <dt className="text-ink-muted">Username</dt>
                <dd className="text-ink">@{query.data.user.username}</dd>
                <dt className="text-ink-muted">Display name</dt>
                <dd className="text-ink">
                  {query.data.user.display_name || "—"}
                </dd>
                <dt className="text-ink-muted">Roles</dt>
                <dd className="text-ink">{query.data.user.role}</dd>
                <dt className="text-ink-muted">Status</dt>
                <dd className={query.data.user.enabled ? "text-good" : "text-ink-muted"}>
                  {query.data.user.enabled ? "Enabled" : "Disabled"}
                </dd>
                <dt className="text-ink-muted">Created</dt>
                <dd className="text-ink">{query.data.user.created_at || "—"}</dd>
                <dt className="text-ink-muted">Last team</dt>
                <dd className="text-ink">{query.data.user.last_team_id || "—"}</dd>
              </dl>
            </section>

            <section data-testid="admin-users-detail-memberships">
              <header className="flex items-baseline justify-between mb-1">
                <h4 className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                  Team memberships ({query.data.memberships.length})
                </h4>
                {/* Re-review P2: section-level pointer to the
                 * `/admin/teams` management surface, matching the
                 * legacy admin-user-detail.js link. Surfaces the
                 * operator path from a user's 360 view to bulk
                 * membership management. */}
                <a
                  href="/admin/teams"
                  data-testid="admin-users-detail-manage-teams"
                  className="text-[0.6rem] font-display uppercase tracking-wider text-accent hover:underline"
                >
                  Manage in Admin · Teams →
                </a>
              </header>
              {query.data.memberships.length === 0 ? (
                <p className="text-[0.6rem] font-mono text-ink-muted">
                  No memberships.
                </p>
              ) : (
                <ul className="flex flex-col gap-1">
                  {query.data.memberships.map((m) => (
                    <li
                      key={m.id}
                      data-testid="admin-users-detail-membership-row"
                      className="grid grid-cols-[1fr_auto] gap-2 items-center rounded border border-rule/60 bg-surface/40 px-2 py-1 font-mono text-[0.65rem]"
                    >
                      <div className="min-w-0 truncate">
                        <span className="text-ink">{m.team_name}</span>{" "}
                        <span className="text-ink-muted">· {m.team_slug}</span>{" "}
                        <span className="text-ink-muted">· {m.role}</span>
                      </div>
                      {/* Per-row deep-link uses the M8b `?team=`
                       * URL persistence so the operator lands on
                       * /admin/teams with the right team selected. */}
                      <a
                        href={`/admin/teams?team=${encodeURIComponent(m.team_id)}`}
                        data-testid="admin-users-detail-membership-manage"
                        data-team-id={m.team_id}
                        className="text-[0.6rem] text-ink-muted hover:text-accent"
                      >
                        Open →
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section data-testid="admin-users-detail-players">
              <h4 className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted mb-1">
                Linked players ({query.data.linked_players.length})
              </h4>
              {query.data.linked_players.length === 0 ? (
                <p className="text-[0.6rem] font-mono text-ink-muted">
                  Not linked to any players.
                </p>
              ) : (
                <ul className="flex flex-col gap-1">
                  {query.data.linked_players.map((p) => (
                    <li
                      key={`${p.player_id}-${p.relationship}`}
                      data-testid="admin-users-detail-player-row"
                      className="rounded border border-rule/60 bg-surface/40 px-2 py-1 font-mono text-[0.65rem]"
                    >
                      <span className="text-ink">{p.player_name}</span>{" "}
                      {p.jersey_number ? (
                        <span className="text-ink-muted">#{p.jersey_number}</span>
                      ) : null}{" "}
                      <span className="text-ink-muted">· {p.team_name}</span>{" "}
                      <span className="text-ink-muted">· {p.relationship}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        ) : null
      }
      onConfirm={onClose}
      onClose={onClose}
      data-testid="admin-users-detail-modal"
    />
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function splitRoleString(role: string | undefined): string[] {
  if (!role) return [];
  return role
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    return typeof err.detail === "string" ? err.detail : `HTTP ${err.status}`;
  }
  return "Request failed";
}
